import React, { useEffect, useState } from "react";
import axios from "../axiosConfig";
import { typeIcons, useFieldOptions, fieldOptionList } from "../components/Utils";
import { useUserPermissions } from "../hooks/useUserPermissions";
import Checkbox from '../components/Checkbox';
import PageTitle from "../components/PageTitle";

const Settings = () => {
  const [user, setUser] = useState(null);
  const fieldOptions = useFieldOptions();
  const { permissions, loading: permissionsLoading } = useUserPermissions();
  const isAdministrator = !permissionsLoading && Boolean(permissions?.is_administrator);

  const [globalVars, setGlobalVars] = useState([]);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newValueError, setNewValueError] = useState("");
  const [globalVarsLoading, setGlobalVarsLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [editingValueError, setEditingValueError] = useState("");

  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser) {
        setUser(storedUser);
      }
    } catch {
      console.warn("Invalid user in localStorage");
    }
  }, []);

  useEffect(() => {
    if (!isAdministrator) return;
    setGlobalVarsLoading(true);
    axios.get("/global-variables/")
      .then((res) => setGlobalVars(res.data.data || []))
      .catch((err) => console.error("Failed to load global variables:", err))
      .finally(() => setGlobalVarsLoading(false));
  }, [isAdministrator]);

  const updateSetting = async (key, value) => {
    if (!user) return;
    try {
      const updatedSettings = {
        ...user.settings,
        [key]: value,
      };

      setUser((prev) => ({
        ...prev,
        settings: updatedSettings,
      }));

      await axios.patch(`/users/${user.id}/`, {
        settings: { [key]: value },
      });

      localStorage.setItem(
        "user",
        JSON.stringify({ ...user, settings: updatedSettings })
      );
    } catch (err) {
      console.error("Failed to update setting:", err);
    }
  };

  const autoResizeTextarea = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const parseValueInput = (raw) => {
    try {
      return { value: JSON.parse(raw), error: "" };
    } catch {
      return { value: null, error: "Invalid JSON" };
    }
  };

  const handleCreateVariable = async () => {
    if (!newKey.trim()) return;
    const { value, error } = parseValueInput(newValue);
    if (error) { setNewValueError(error); return; }
    setNewValueError("");
    try {
      await axios.post("/global-variables/", { key: newKey.trim(), value });
      const res = await axios.get("/global-variables/");
      setGlobalVars(res.data.data || []);
      setNewKey("");
      setNewValue("");
    } catch (err) {
      console.error("Failed to create global variable:", err);
    }
  };

  const handleSaveEdit = async (key) => {
    const { value, error } = parseValueInput(editingValue);
    if (error) { setEditingValueError(error); return; }
    setEditingValueError("");
    try {
      await axios.patch(`/global-variables/${key}/`, { value });
      setGlobalVars((prev) =>
        prev.map((v) => (v.key === key ? { ...v, value } : v))
      );
      setEditingKey(null);
    } catch (err) {
      console.error("Failed to update global variable:", err);
    }
  };

  const handleDeleteVariable = async (key) => {
    try {
      await axios.delete(`/global-variables/${key}/`);
      setGlobalVars((prev) => prev.filter((v) => v.key !== key));
    } catch (err) {
      console.error("Failed to delete global variable:", err);
    }
  };

  if (!user) {
    return <div><p>Loading user...</p></div>;
  }

  const { settings } = user;

  return (
    <div className="page-layout">
      <PageTitle>{typeIcons.settings} Settings</PageTitle>

      <div className="page-content">
        <h3>User Preferences</h3>

        <table>
          <tbody>
            <tr>
              <td><strong>Preferred Interaction Format:</strong></td>
              <td>
                <select autoComplete="off"
                  value={settings.default_interaction_format}
                  onChange={(e) => updateSetting("default_interaction_format", e.target.value)}
                >
                  <option value="">None</option>
                  {fieldOptionList(fieldOptions, "interactions", "format").map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </td>
            </tr>
            <tr>
              <td><strong>Preferred Interaction Type:</strong></td>
              <td>
                <select autoComplete="off"
                  value={settings.default_interaction_type}
                  onChange={(e) => updateSetting("default_interaction_type", e.target.value)}
                >
                  <option value="">None</option>
                  {fieldOptionList(fieldOptions, "interactions", "type").map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </td>
            </tr>
            <tr>
              <td><strong>Enable Notifications:</strong></td>
              <td>
                <div className="checkbox-group">
                  <label>
                    <Checkbox
                      checked={settings.notifications_enabled}
                      onChange={(e) => updateSetting("notifications_enabled", e.target.checked)}
                    />
                    {" "}(doesn't do anything yet)
                  </label>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

      {isAdministrator && (
        <div style={{ marginTop: "2rem" }}>
          <h3>Global Variables</h3>
          <p style={{ color: "var(--color-muted, #666)", marginBottom: "1rem" }}>
            These variables are stored globally and can be read by all users. Only administrators can modify them.
          </p>

          {globalVarsLoading ? (
            <p>Loading...</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Value (JSON)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {globalVars.map((v) => (
                  <tr key={v.key}>
                    <td><code>{v.key}</code></td>
                    <td>
                      {editingKey === v.key ? (
                        <div>
                          <textarea autoComplete="off"
                            ref={(el) => { if (el) autoResizeTextarea(el); }}
                            rows={1}
                            style={{ width: "100%", fontFamily: "monospace", resize: "none", overflow: "hidden" }}
                            value={editingValue}
                            onChange={(e) => { setEditingValue(e.target.value); setEditingValueError(""); }}
                            onInput={(e) => autoResizeTextarea(e.target)}
                          />
                          {editingValueError && <div style={{ color: "red", fontSize: "0.85em" }}>{editingValueError}</div>}
                        </div>
                      ) : (
                        <code style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {JSON.stringify(v.value, null, 2)}
                        </code>
                      )}
                    </td>
                    <td>
                      {editingKey === v.key ? (
                        <>
                          <button onClick={() => handleSaveEdit(v.key)}>Save</button>
                          {" "}
                          <button onClick={() => { setEditingKey(null); setEditingValueError(""); }}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => { setEditingKey(v.key); setEditingValue(JSON.stringify(v.value, null, 2)); setEditingValueError(""); }}>Edit</button>
                          {" "}
                          <button onClick={() => handleDeleteVariable(v.key)}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td>
                    <input autoComplete="off"
                      type="text"
                      placeholder="key"
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                    />
                  </td>
                  <td>
                    <textarea autoComplete="off"
                      rows={1}
                      style={{ width: "100%", fontFamily: "monospace", resize: "none", overflow: "hidden" }}
                      placeholder='{"text": "Example"}'
                      value={newValue}
                      onChange={(e) => { setNewValue(e.target.value); setNewValueError(""); }}
                      onInput={(e) => autoResizeTextarea(e.target)}
                    />
                    {newValueError && <div style={{ color: "red", fontSize: "0.85em" }}>{newValueError}</div>}
                  </td>
                  <td>
                    <button onClick={handleCreateVariable}>Add</button>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default Settings;
